import { motion } from "motion/react";
import styled from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import { greyMui, rydeColors, rydeColorsGraphs } from "@ryde/theme";

import { DRAGGING_CARD_OPACITY } from "./dynamic-dashboard.constants";

/** 3-column responsive grid; collapses to 2 / 1 columns on smaller widths. */
export const DashboardGridStyled = styled(motion.div).attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "dashboard-grid",
})`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  padding: 16px 24px 48px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/**
 * Card frame in the spirit of the Rides card, but grid-sized (size varies per
 * chart). The header / body / empty-state are plain nested elements addressed
 * by className (`.dashboard-card-header`, `.dashboard-card-body`,
 * `.dashboard-card-empty`) rather than separate styled components — so all of
 * the card's styling lives in one place.
 */
export const DashboardCardStyled = styled(motion.div).attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "dashboard-card",
})<{
  $isDragging?: boolean;
  $isOver?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background-color: ${rydeColors.white};
  border: 1px solid ${greyMui._300};
  border-radius: 6px;
  box-shadow: 0px 2px 3px rgba(0, 0, 0, 0.15);
  opacity: ${({ $isDragging }) => ($isDragging ? DRAGGING_CARD_OPACITY : 1)};
  outline: ${({ $isOver }) =>
    $isOver
      ? `2px dashed ${rydeColorsGraphs.verticalBarDefaultColor}`
      : "none"};
  outline-offset: 2px;

  &.wide {
    grid-column: 1 / -1;
  }

  .dashboard-card-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-bottom: 1px solid ${greyMui._300};
  }

  .grip {
    display: inline-flex;
    align-items: center;
    padding: 0;
    border: none;
    background: transparent;
    cursor: grab;
    touch-action: none;
  }

  .grip:active {
    cursor: grabbing;
  }

  .grip svg {
    width: 16px;
    height: 16px;
  }

  .titles {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .title {
    font-size: 14px;
    font-weight: 600;
    color: ${greyMui._700};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .subtitle {
    font-size: 12px;
    color: ${greyMui._500};
  }

  .spacer {
    flex: 1 1 auto;
  }

  .icon-btn {
    display: inline-flex;
    align-items: center;
    padding: 4px;
    border: none;
    background: transparent;
    color: ${greyMui._500};
    cursor: pointer;
  }

  .icon-btn svg {
    width: 16px;
    height: 16px;
  }

  .icon-btn:hover {
    color: ${greyMui._700};
  }

  /* Fixed-height body — the charts are responsive with maintainAspectRatio:false. */
  .dashboard-card-body {
    position: relative;
    height: 240px;
    padding: 8px 12px 12px;
  }

  .dashboard-card-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: ${greyMui._500};
    font-size: 13px;
  }
`;
